import { memo, useCallback, useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import {
  X,
  Maximize2,
  Network,
  LayoutGrid,
  Link2,
  ArrowRight,
  ExternalLink,
  Copy,
  Check,
  Loader2
} from 'lucide-react'
import { Sheet, SheetContent } from '@/components/ui/sheet'
import { Button } from '@/components/ui/button'
import { ScrollArea } from '@/components/ui/scroll-area'
import { useNavigationStore, type PreviewableItem } from '@/stores/navigationStore'
import { useLibraryConnections } from '@/hooks/useLibrary'
import { useToast } from '@/components/ui/toast'
import { TYPE_COLORS, TYPE_LABELS, TYPE_ICONS } from '@/types/library'

function PreviewPanelComponent() {
  const navigate = useNavigate()
  const { previewItem, setPreview } = useNavigationStore()
  const { connections, backlinks, loading } = useLibraryConnections(previewItem?.id || null)
  const [copied, setCopied] = useState(false)
  const toast = useToast()

  const handleClose = useCallback(() => {
    setPreview(null)
  }, [setPreview])

  const handleExpand = useCallback(() => {
    if (previewItem) {
      navigate(`/dive/${previewItem.id}`)
      setPreview(null)
    }
  }, [previewItem, navigate, setPreview])

  const handleViewInGraph = useCallback(() => {
    if (previewItem) {
      navigate(`/constellation?focus=${previewItem.id}`)
      setPreview(null)
    }
  }, [previewItem, navigate, setPreview])

  const handleAddToCanvas = useCallback(() => {
    if (previewItem) {
      navigate(`/canvas?import=${previewItem.id}`)
      setPreview(null)
    }
  }, [previewItem, navigate, setPreview])

  const handleCopyId = useCallback(() => {
    if (previewItem) {
      navigator.clipboard.writeText(previewItem.id)
      setCopied(true)
      toast.info('ID copiado', previewItem.id.slice(0, 8) + '...')
      setTimeout(() => setCopied(false), 2000)
    }
  }, [previewItem, toast])

  const handleConnectionClick = useCallback((id: string, name: string, type: string) => {
    setPreview({
      id,
      type: type as PreviewableItem['type'],
      name
    })
  }, [setPreview])

  // Reset copied state when item changes
  useEffect(() => {
    const timer = setTimeout(() => setCopied(false), 0)
    return () => clearTimeout(timer)
  }, [previewItem?.id])

  if (!previewItem) return null

  const color = TYPE_COLORS[previewItem.type] || '#8B5CF6'

  return (
    <Sheet open={!!previewItem} onOpenChange={(open) => !open && handleClose()}>
      <SheetContent
        side="right"
        className="w-full sm:w-[420px] p-0 bg-rsb-bg/98 backdrop-blur-xl border-l border-white/10"
      >
        {/* Accent bar */}
        <div
          className="h-1"
          style={{ background: `linear-gradient(90deg, ${color}, ${color}40)` }}
        />

        {/* Header */}
        <div className="sticky top-0 z-10 bg-rsb-bg/95 backdrop-blur-sm border-b border-white/5">
          <div className="p-4">
            {/* Top row with close and actions */}
            <div className="flex items-center justify-between mb-3">
              <span
                className="inline-flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-wider px-2.5 py-1 rounded-full"
                style={{ backgroundColor: `${color}20`, color }}
              >
                {TYPE_ICONS[previewItem.type]} {TYPE_LABELS[previewItem.type]}
              </span>

              <div className="flex items-center gap-1">
                <button
                  onClick={handleCopyId}
                  className="p-2 rounded-lg hover:bg-white/5 text-white/40 hover:text-white transition-colors"
                  title="Copiar ID"
                >
                  {copied ? <Check className="w-4 h-4 text-green-400" /> : <Copy className="w-4 h-4" />}
                </button>
                <button
                  onClick={handleClose}
                  className="p-2 rounded-lg hover:bg-white/5 text-white/40 hover:text-white transition-colors"
                >
                  <X className="w-4 h-4" />
                </button>
              </div>
            </div>

            {/* Title */}
            <h2 className="text-lg font-semibold text-white leading-tight mb-4 pr-8">
              {previewItem.name}
            </h2>

            {/* Quick Actions */}
            <div className="flex gap-2">
              <Button
                size="sm"
                onClick={handleExpand}
                className="flex-1 gap-1.5 bg-rsb-accent/20 hover:bg-rsb-accent/30 text-rsb-accent border-rsb-accent/30"
              >
                <Maximize2 className="w-3.5 h-3.5" />
                Expandir
              </Button>
              <Button
                size="sm"
                variant="outline"
                onClick={handleViewInGraph}
                className="gap-1.5 border-white/10 hover:bg-white/5"
                title="Ver no Grafo"
              >
                <Network className="w-3.5 h-3.5" />
              </Button>
              <Button
                size="sm"
                variant="outline"
                onClick={handleAddToCanvas}
                className="gap-1.5 border-white/10 hover:bg-white/5"
                title="Adicionar ao Canvas"
              >
                <LayoutGrid className="w-3.5 h-3.5" />
              </Button>
            </div>
          </div>
        </div>

        <ScrollArea className="h-[calc(100vh-220px)]">
          <div className="p-4 space-y-6">
            {/* Domain badge */}
            {previewItem.domain && (
              <div className="flex items-center gap-2">
                <span
                  className="px-2.5 py-1 rounded-full text-xs font-medium"
                  style={{
                    backgroundColor: `${previewItem.domain.color}20`,
                    color: previewItem.domain.color
                  }}
                >
                  {previewItem.domain.name}
                </span>
              </div>
            )}

            {/* Content */}
            {previewItem.content && (
              <div>
                <h4 className="text-[10px] font-bold text-white/40 uppercase tracking-wider mb-2">
                  Conteúdo
                </h4>
                <p className="text-sm text-white/80 leading-relaxed whitespace-pre-wrap">
                  {previewItem.content}
                </p>
              </div>
            )}

            {/* Connections */}
            <div>
              <h4 className="text-[10px] font-bold text-white/40 uppercase tracking-wider mb-3 flex items-center gap-2">
                <Link2 className="w-3.5 h-3.5" />
                Conexões de Saída
                {loading ? (
                  <Loader2 className="w-3 h-3 animate-spin" />
                ) : (
                  <span className="text-white/20">({connections.length})</span>
                )}
              </h4>

              {loading ? (
                <div className="space-y-2">
                  {[1, 2, 3].map(i => (
                    <div key={i} className="h-12 bg-white/5 rounded-lg animate-pulse" />
                  ))}
                </div>
              ) : connections.length === 0 ? (
                <p className="text-xs text-white/30 italic py-3">Sem conexões de saída</p>
              ) : (
                <div className="space-y-1.5">
                  {connections.map(conn => (
                    <button
                      key={conn.id}
                      onClick={() => handleConnectionClick(conn.to_id, conn.to_name, conn.to_type)}
                      className="w-full flex items-center gap-3 p-3 rounded-xl bg-white/5 hover:bg-white/10 transition-all text-left group border border-transparent hover:border-white/10"
                    >
                      <ArrowRight className="w-3.5 h-3.5 text-white/30 flex-shrink-0" />
                      <div className="flex-1 min-w-0">
                        <span className="text-sm text-white truncate block">
                          {conn.to_name}
                        </span>
                        <span className="text-[10px] text-white/30 uppercase">
                          {conn.connection_type}
                        </span>
                      </div>
                      <div className="flex items-center gap-2 flex-shrink-0">
                        <div className="w-12 h-1.5 bg-white/10 rounded-full overflow-hidden">
                          <div
                            className="h-full rounded-full bg-rsb-accent/60"
                            style={{ width: `${conn.weight * 100}%` }}
                          />
                        </div>
                        <span className="text-[10px] text-white/30 w-8">
                          {Math.round(conn.weight * 100)}%
                        </span>
                      </div>
                      <ExternalLink className="w-3.5 h-3.5 text-white/20 group-hover:text-white/50 flex-shrink-0" />
                    </button>
                  ))}
                </div>
              )}
            </div>

            {/* Backlinks */}
            <div>
              <h4 className="text-[10px] font-bold text-white/40 uppercase tracking-wider mb-3 flex items-center gap-2">
                <ArrowRight className="w-3.5 h-3.5 rotate-180" />
                Referenciado por
                <span className="text-white/20">({backlinks.length})</span>
              </h4>

              {backlinks.length === 0 ? (
                <p className="text-xs text-white/30 italic py-3">Nenhum backlink</p>
              ) : (
                <div className="space-y-1.5">
                  {backlinks.map(link => (
                    <button
                      key={link.id}
                      onClick={() => handleConnectionClick(link.id, link.name, link.type)}
                      className="w-full flex items-center gap-3 p-3 rounded-xl bg-white/5 hover:bg-white/10 transition-all text-left group border border-transparent hover:border-white/10"
                    >
                      <span
                        className="text-sm flex-shrink-0"
                        style={{ color: link.domain?.color || '#8B5CF6' }}
                      >
                        {TYPE_ICONS[link.type]}
                      </span>
                      <span className="flex-1 text-sm text-white truncate">
                        {link.name}
                      </span>
                      {link.domain && (
                        <span
                          className="text-[10px] px-2 py-0.5 rounded-full flex-shrink-0"
                          style={{
                            backgroundColor: `${link.domain.color}15`,
                            color: link.domain.color
                          }}
                        >
                          {link.domain.name}
                        </span>
                      )}
                    </button>
                  ))}
                </div>
              )}
            </div>

            {/* Metadata */}
            {previewItem.metadata && Object.keys(previewItem.metadata).length > 0 && (
              <div>
                <h4 className="text-[10px] font-bold text-white/40 uppercase tracking-wider mb-2">
                  Metadados
                </h4>
                <div className="space-y-1 text-xs">
                  {Object.entries(previewItem.metadata).map(([key, value]) => (
                    <div key={key} className="flex justify-between py-1">
                      <span className="text-white/40">{key}</span>
                      <span className="text-white/60">{String(value)}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </ScrollArea>
      </SheetContent>
    </Sheet>
  )
}

export const PreviewPanel = memo(PreviewPanelComponent)
